import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { canonicalFilterField } from 'modules/candidate-profile/tables/filters/filterFieldAliases';
import { isCondition } from 'modules/candidate-profile/tables/filters/parseCandidateFilterTree';
import {
  CandidateFilterCondition,
  CandidateFilterTypeKey,
  CandidateFilterMatchMode,
  CandidateFilterNode,
  CandidateFilterValueType,
  CandidateSelectionFilter
} from 'modules/candidate-profile/types/CandidateSearchFilter';

/**
 * The talent pool the page shows, as a filter condition, so it goes on every
 * request and not only on the first load.
 *
 * ADD_CANDIDATES_TO_TALENT_POOL has none on purpose: it lists the candidates
 * you could add, not the ones already there.
 */
export const getTalentPoolCondition = (
  candidateProfileListProps: CandidateProfileListProps
): CandidateSelectionFilter | null => {
  const showsTalentPool =
    candidateProfileListProps.type ===
      CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL ||
    candidateProfileListProps.type ===
      CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL;

  if (!showsTalentPool) return null;

  return {
    id: `talent-pool-${candidateProfileListProps.talentPoolId}`,
    field: 'candidateProfileLists',
    type: CandidateFilterValueType.TEXT_ARRAY,
    mode: CandidateFilterMatchMode.INCLUDE_ANY,
    values: [candidateProfileListProps.talentPoolId]
  };
};

/**
 * The fields a chat turn is allowed to drop from its request.
 *
 * The page's talent pool is never one of them. It is not a filter the
 * recruiter chose, it goes on every request, and a turn that left it out would
 * search outside the talent pool the page is showing. A reply is free to offer a suggestion
 * naming it -- the field is in the tree like any other -- so the request, not
 * the suggestion, is where that is refused.
 */
export const removableFields = (
  fields: string[],
  candidateProfileListProps: CandidateProfileListProps
): string[] => {
  const talentPool = getTalentPoolCondition(candidateProfileListProps);
  if (!talentPool) return fields;

  return fields.filter(
    (field) => canonicalFilterField(field) !== talentPool.field
  );
};

const isTalentPoolCondition = (
  node: CandidateFilterCondition,
  talentPoolId: string
): boolean =>
  canonicalFilterField(node.field, CandidateFilterTypeKey.TEXT_ARRAY) ===
    'candidateProfileLists' &&
  node.textArray?.containsAny?.length === 1 &&
  node.textArray.containsAny[0] === talentPoolId;

/**
 * The tree a search ran, without the talent pool condition this page added.
 *
 * The talent pool goes on every request and comes back in `appliedFilter` like
 * any other condition. As a chip it would offer to remove something the next
 * request adds again, so it is taken out before the chips see it.
 */
export const withoutTalentPoolFilter = (
  tree: CandidateFilterNode | null | undefined,
  candidateProfileListProps: CandidateProfileListProps
): CandidateFilterNode | null => {
  const talentPool = getTalentPoolCondition(candidateProfileListProps);
  if (!tree || !talentPool) return tree ?? null;

  const [talentPoolId] = talentPool.values;

  const remove = (node: CandidateFilterNode): CandidateFilterNode | null => {
    if (isCondition(node)) {
      return isTalentPoolCondition(node, talentPoolId) ? null : node;
    }

    if (node.and) {
      const children = node.and
        .map(remove)
        .filter((child): child is CandidateFilterNode => child !== null);

      return children.length ? { and: children } : null;
    }

    // An `or` or `not` branch never holds the talent pool, and taking a
    // condition out of one would change what the rest matches.
    return node;
  };

  return remove(tree);
};
